import HttpApi from "i18next-http-backend"
import spacetime, { SpacetimeWithI18n } from 'spacetime'
import { warn } from 'utils'

const updateSpacetimeI18n = (i18n) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const months = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ]

  const run = () => {
    (spacetime(null) as SpacetimeWithI18n).i18n({
      days: {
        long: days.map(day => i18n.t(`dt.day.${day}`)),
        short: days.map(day => i18n.t(`dt.short.day.${day}`))
      },
      months: {
        long: months.map(month => i18n.t(`dt.month.${month}`)),
        short: months.map(month => i18n.t(`dt.short.month.${month}`))
      },
      useTitleCase:false
    })
  }

  run()
}

export const normalizeLanguage = (language: string): string => {
  if (!language) {
    return null
  }

  const langsToNormalize = [
    ['de-.*', 'de'],
    ['fr-.*', 'fr'],
    ['es-.*', 'es'],
    ['pt', 'pt-PT']
  ]

  for (let [pattern, normalizedLang] of langsToNormalize) {
    if (new RegExp(`^${pattern}`).test(language)) {
      return normalizedLang
    }
  }

  return language
}

const importByLocale = (locale = 'en'): Promise<Record<string, any>> => {
  switch (locale) {
    case 'da':
      return import('../translations/da-DK.json')

    case 'de':
      return import('../translations/de.json')

    case 'es':
      return import('../translations/es-ES.json')

    case 'et':
      return import('../translations/et-EE.json')

    case 'fi':
      return import('../translations/fi-FI.json')

    case 'fr':
      return import('../translations/fr-FR.json')

    case 'hi':
      return import('../translations/hi-IN.json')

    case 'hr':
      return import('../translations/hr-HR.json')

    case 'hu':
      return import('../translations/hu-HU.json')

    case 'is':
      return import('../translations/is-IS.json')

    case 'it':
      return import('../translations/it-IT.json')

    case 'ja':
      return import('../translations/ja-JP.json')

    // ka-GE translation not complete
    // case 'ka':
    //   return import('../translations/ka-GE.json')

    case 'ko':
      return import('../translations/ko-KR.json')

    case 'nb':
      return import('../translations/nb-NO.json')

    case 'nl':
      return import('../translations/nl-NL.json')

    case 'pl':
      return import('../translations/pl-PL.json')

    case 'pt-PT':
      return import('../translations/pt-PT.json')

    case 'pt-BR':
      return import('../translations/pt-BR.json')

    case 'ro':
      return import('../translations/ro-RO.json')

    case 'ru':
      return import('../translations/ru-RU.json')

    case 'sl':
      return import('../translations/sl-SI.json')

    // Serbian translation currently not correct
    // case 'sr':
    //   return import('../translations/sr-SP.json')

    case 'sv':
      return import('../translations/sv-SE.json')

    case 'th':
      return import('../translations/th-TH.json')

    case 'tr':
      return import('../translations/tr-TR.json')

    case 'vi':
      return import('../translations/vi-VN.json')

    case 'zh':
      return import('../translations/zh-Hans-CN.json')

    case 'zh-HK':
      return import('../translations/zh-HK.json')

    default:
      return import('../translations/en-US.json')
  }
}

const initI18n = async (locale?: string) => {
  const { default: i18n } = require('i18next')
  const { initReactI18next } = require('react-i18next')
  const selectedLocale = normalizeLanguage(locale) || 'en'

  await i18n
    .use(HttpApi)
    .use(initReactI18next)
    .init({
      lng: selectedLocale,
      fallbackLng: 'en',
      nsSeparator: false,
      interpolation: {
        escapeValue: false,
      },
      initImmediate: false,
      react: {
        wait: true
      },
      backend: {
        loadPath: '{{lng}}',
        request: async (options, url, payload, callback) => {
          try {
            const translationData = await importByLocale(url)
            callback(null, {
              data: JSON.stringify(translationData),
              status: 200 // force a 200 on fetching from the bundle
            })
          } catch (err) {
            warn([`Failed to fetch translations for locale: ${locale}`])
            callback(null, {
              status: 500
            })
          }
        }
      }
    })

    i18n.on('languageChanged', () => updateSpacetimeI18n(i18n))
}

export { initI18n }
